import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PercentIcon from '@mui/icons-material/Percent';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

/** Cor para % de execucao */
const getExecucaoColor = (pct: number): string => {
  if (pct >= 90) return '#27AE60';
  if (pct >= 70) return '#F39C12';
  return '#E74C3C';
};

/** Transparencia simples via rgba */
const alphaColor = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const MonitoramentoFpo: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getFpo(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de auditoria FPO.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  // --- Dados derivados ---
  const programadoVsRealizadoRaw: any[] = useMemo(() => data?.programadoVsRealizado || [], [data]);
  const cotasPrestadoresRaw: any[] = data?.cotasPrestadores || [];
  const evolucaoRaw: any[] = data?.evolucao || [];

  // Converter strings para numbers no programadoVsRealizado
  const programadoChart = useMemo(
    () =>
      programadoVsRealizadoRaw.map((item: any) => ({
        nome_procedimento: item.nome_procedimento,
        codigo_procedimento: item.codigo_procedimento,
        valor_programado: Number(item.valor_programado ?? 0),
        valor_realizado: Number(item.valor_realizado ?? 0),
        quantidade_programada: Number(item.quantidade_programada ?? 0),
        quantidade_realizada: Number(item.quantidade_realizada ?? 0),
        pct_execucao_qtd: Number(item.pct_execucao_qtd ?? 0),
        pct_execucao_valor: Number(item.pct_execucao_valor ?? 0),
      })),
    [programadoVsRealizadoRaw],
  );

  // KPIs calculados a partir dos dados
  const kpisCalc = useMemo(() => {
    const totalProgramado = programadoChart.reduce((acc, r) => acc + r.valor_programado, 0);
    const totalRealizado = programadoChart.reduce((acc, r) => acc + r.valor_realizado, 0);
    const pctExecucao = totalProgramado > 0 ? (totalRealizado / totalProgramado) * 100 : 0;
    return { totalProgramado, totalRealizado, pctExecucao };
  }, [programadoChart]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de auditoria FPO.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Auditoria FPO
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs calculados */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Total Programado"
            valor={`R$ ${kpisCalc.totalProgramado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            icone={<AssignmentIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Total Realizado"
            valor={`R$ ${kpisCalc.totalRealizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            icone={<FactCheckIcon />}
            cor="#27AE60"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="% Execucao"
            valor={`${kpisCalc.pctExecucao.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
            icone={<PercentIcon />}
            cor={getExecucaoColor(kpisCalc.pctExecucao)}
          />
        </Grid>
      </Grid>

      {/* Programado vs Realizado - BarChart agrupado */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Programado vs Realizado por Procedimento
        </Typography>
        {programadoChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={programadoChart} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="nome_procedimento"
                tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }}
                height={80}
              />
              <YAxis tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  name === 'valor_programado' ? 'Programado' : 'Realizado',
                ]}
              />
              <Legend />
              <Bar dataKey="valor_programado" name="Programado" fill="#1E3A5F" radius={[4, 4, 0, 0]} />
              <Bar dataKey="valor_realizado" name="Realizado" fill="#27AE60" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de programado vs realizado para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Cotas por prestador */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Cotas por Prestador
        </Typography>
        {cotasPrestadoresRaw.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(cotasPrestadoresRaw.length * 40, 250)}>
            <BarChart
              data={cotasPrestadoresRaw.map((item: any) => ({
                prestador: item.nome_prestador || item.prestador,
                cota: Number(item.cota ?? item.valor_programado ?? 0),
                utilizado: Number(item.utilizado ?? item.valor_realizado ?? 0),
              }))}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <YAxis
                type="category"
                dataKey="prestador"
                width={200}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  name === 'cota' ? 'Cota' : 'Utilizado',
                ]}
              />
              <Legend />
              <Bar dataKey="cota" name="Cota" fill="#1E3A5F" radius={[0, 4, 4, 0]} />
              <Bar dataKey="utilizado" name="Utilizado" fill="#F39C12" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de cotas por prestador para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Evolucao temporal */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Serie Temporal
        </Typography>
        {evolucaoRaw.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={(() => {
                // Agrupar por mes/competencia
                const map = new Map<string, { programado: number; realizado: number }>();
                for (const item of evolucaoRaw) {
                  const mes = String(item.competencia ?? item.mes ?? item.anomescompetencia ?? '');
                  if (!map.has(mes)) {
                    map.set(mes, { programado: 0, realizado: 0 });
                  }
                  const row = map.get(mes)!;
                  row.programado += Number(item.valor_programado ?? item.programado ?? 0);
                  row.realizado += Number(item.valor_realizado ?? item.realizado ?? 0);
                }
                return Array.from(map.entries())
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([mes, vals]) => ({
                    competencia: mes,
                    Programado: Math.round(vals.programado * 100) / 100,
                    Realizado: Math.round(vals.realizado * 100) / 100,
                  }));
              })()}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="competencia" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  name,
                ]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Programado"
                name="Programado"
                stroke="#1E3A5F"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="Realizado"
                name="Realizado"
                stroke="#27AE60"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de serie temporal para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Tabela detalhada */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Detalhamento por Procedimento
        </Typography>
        {programadoChart.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd Prog.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd Real.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Prog. (R$)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Real. (R$)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>% Exec. Qtd</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>% Exec. Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programadoChart.map((row: any, index: number) => {
                  const corQtd = getExecucaoColor(row.pct_execucao_qtd);
                  const corVal = getExecucaoColor(row.pct_execucao_valor);
                  return (
                    <TableRow key={index}>
                      <TableCell>{row.nome_procedimento}</TableCell>
                      <TableCell align="right">
                        {row.quantidade_programada.toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="right">
                        {row.quantidade_realizada.toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="right">
                        {row.valor_programado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="right">
                        {row.valor_realizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${row.pct_execucao_qtd.toFixed(1)}%`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            minWidth: 60,
                            backgroundColor: alphaColor(corQtd, 0.15),
                            color: corQtd,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${row.pct_execucao_valor.toFixed(1)}%`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            minWidth: 60,
                            backgroundColor: alphaColor(corVal, 0.15),
                            color: corVal,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados detalhados para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="fpo"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoFpo;
